import type { SystemTags } from '@hewey/shared'

// Tag pills derived from systemTags. Drops "unknown"/"other"/"none" so the
// strip stays a useful signal — "Indian", "Chicken", "Dinner" beats "other
// unknown none". Order is cuisine → protein → carb → sauce → type → effort →
// batch cook, putting the most-distinctive label first.

export type TagPillKind =
	'cuisine' | 'protein' | 'carb' | 'sauce' | 'type' | 'effort' | 'batchCook'

export type TagPill = {
	label: string
	kind: TagPillKind
}

// Pill background per kind. The palette's six darker accents carry white pill
// text; batch cook takes the remaining one, sunshine, which needs dark text.
export const KIND_COLOUR: Record<TagPillKind, string> = {
	cuisine: 'var(--accent-sky)',
	protein: 'var(--accent-rose)',
	carb: 'var(--accent-apricot)',
	sauce: 'var(--accent-lilac)',
	type: 'var(--accent-terracotta)',
	effort: 'var(--accent-sage)',
	batchCook: 'var(--accent-sunshine)',
}

// Overrides the pill's default white text. Absent kinds keep the stylesheet's.
export const KIND_INK: Partial<Record<TagPillKind, string>> = {
	batchCook: 'var(--ink)',
}

// Exported so the filter bar can offer the same meaningful labels as the card
// pills (noise values — unknown/other/none — are simply absent here).
export const cleanCuisine: Partial<Record<SystemTags['cuisine'], string>> = {
	italian: 'Italian',
	indian: 'Indian',
	chinese: 'Chinese',
	thai: 'Thai',
	mexican: 'Mexican',
	middle_eastern: 'Middle Eastern',
	spanish: 'Spanish',
	french: 'French',
	british: 'British',
	american: 'American',
	scandinavian: 'Scandi',
	japanese: 'Japanese',
	european: 'European',
	east_european: 'East European',
	south_american: 'South American',
	texan: 'Texan',
	malaysian: 'Malaysian',
	singaporean: 'Singaporean',
}

export const cleanProtein: Partial<
	Record<SystemTags['primaryProtein'], string>
> = {
	chicken: 'Chicken',
	red_meat: 'Red meat',
	fish_white: 'White fish',
	beans_legumes: 'Beans',
}

export const cleanCarb: Partial<Record<SystemTags['primaryCarb'], string>> = {
	rice: 'Rice',
	pasta: 'Pasta',
	potato: 'Potato',
	bread_wrap: 'Bread',
	noodles: 'Noodles',
	grains: 'Grains',
}

export const cleanSauce: Partial<Record<SystemTags['sauceBase'], string>> = {
	tomato: 'Tomato',
	cream: 'Cream',
	coconut: 'Coconut',
	soy_umami: 'Soy',
	broth_stock: 'Broth',
	dry_spiced: 'Dry-spiced',
	dressing_based: 'Dressing',
}

export const cleanType: Partial<Record<SystemTags['type'], string>> = {
	breakfast: 'Breakfast',
	lunch: 'Lunch',
	dinner: 'Dinner',
	dessert: 'Dessert',
	snack: 'Snack',
}

export const cleanEffort: Partial<Record<SystemTags['effort'], string>> = {
	low: 'Low effort',
	medium: 'Medium effort',
	high: 'High effort',
}

// Only 'yes' earns a pill — "not a batch cook" isn't worth the space.
export const cleanBatchCook: Partial<Record<SystemTags['batchCook'], string>> =
	{
		yes: 'Batch cook',
	}

export const tagPills = (tags: SystemTags): TagPill[] => {
	const pills: TagPill[] = []
	const cuisine = cleanCuisine[tags.cuisine]
	if (cuisine) pills.push({ label: cuisine, kind: 'cuisine' })
	const protein = cleanProtein[tags.primaryProtein]
	if (protein) pills.push({ label: protein, kind: 'protein' })
	const carb = cleanCarb[tags.primaryCarb]
	if (carb) pills.push({ label: carb, kind: 'carb' })
	const sauce = cleanSauce[tags.sauceBase]
	if (sauce) pills.push({ label: sauce, kind: 'sauce' })
	const type = cleanType[tags.type]
	if (type) pills.push({ label: type, kind: 'type' })
	const effort = cleanEffort[tags.effort]
	if (effort) pills.push({ label: effort, kind: 'effort' })
	const batchCook = cleanBatchCook[tags.batchCook]
	if (batchCook) pills.push({ label: batchCook, kind: 'batchCook' })
	return pills
}

// The card's time/effort strip reuses the title-case effort labels; `null`
// for unknown so the card omits it.
export const effortLabel = (effort: SystemTags['effort']): string | null =>
	cleanEffort[effort] ?? null
